import * as React from 'react';
import { cn } from '../../lib/utils';
import { useHorizontalOverflow } from '../../hooks/use-horizontal-overflow';

/**
 * Table — dense, hairline-ruled table primitives for operator and admin
 * inventories (fleet, warehouse, release artifacts, audit trails).
 *
 * Rules (see `.impeccable.md` — "density before whitespace", "hairlines, not
 * heavy borders"):
 *   - no outer card, no rounded box, no zebra striping; rows are separated by
 *     a single hairline and the header by a strong hairline
 *   - header labels are mono, uppercase, tracked and quiet; data is tabular
 *   - `density="compact"` (default) gives ~36px rows; `comfortable` ~44px
 *   - the scroll container is the only thing allowed to overflow the page.
 *     When it overflows it becomes keyboard-focusable (WCAG 2.1.1) and fades
 *     its trailing edge so the hidden columns are discoverable.
 */

type TableDensity = 'compact' | 'comfortable';

const TableDensityContext = React.createContext<TableDensity>('compact');

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
  /** Class for the scroll container that wraps the `<table>`. */
  containerClassName?: string;
  /**
   * Accessible name for the scroll region. Required for the region to be
   * announced when it overflows; falls back to the table's `aria-label`.
   */
  scrollLabel?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, density = 'compact', scrollLabel, ...props }, ref) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const { overflowing, atEnd } = useHorizontalOverflow(containerRef);
    const regionLabel = scrollLabel ?? props['aria-label'];

    return (
      <TableDensityContext.Provider value={density}>
        <div
          ref={containerRef}
          data-slot="table-container"
          data-overflowing={overflowing ? '' : undefined}
          data-fade={overflowing && !atEnd ? 'end' : undefined}
          {...(overflowing
            ? {
                tabIndex: 0,
                role: 'region',
                ...(regionLabel ? { 'aria-label': regionLabel } : {}),
              }
            : {})}
          className={cn(
            'relative w-full overflow-x-auto overscroll-x-contain',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
            'data-[fade=end]:[mask-image:linear-gradient(to_right,black_calc(100%_-_2.5rem),transparent)]',
            'rtl:data-[fade=end]:[mask-image:linear-gradient(to_left,black_calc(100%_-_2.5rem),transparent)]',
            containerClassName,
          )}
        >
          <table
            ref={ref}
            data-slot="table"
            data-density={density}
            className={cn(
              'w-full caption-bottom border-collapse text-sm tabular-nums text-[var(--color-foreground)]',
              className,
            )}
            {...props}
          />
        </div>
      </TableDensityContext.Provider>
    );
  },
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    data-slot="table-header"
    className={cn('[&_tr]:border-b [&_tr]:border-[var(--color-hairline-strong)]', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    data-slot="table-body"
    className={cn('[&_tr:last-child]:border-b-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    data-slot="table-footer"
    className={cn(
      'border-t border-[var(--color-hairline-strong)] text-[var(--color-foreground-2)]',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      data-slot="table-row"
      className={cn(
        'border-b border-[var(--color-hairline)] transition-colors',
        'hover:bg-[var(--color-surface-2)]',
        'aria-selected:bg-[var(--color-accent-tint)] data-[state=selected]:bg-[var(--color-accent-tint)]',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const headDensity: Record<TableDensity, string> = {
  compact: 'h-8',
  comfortable: 'h-10',
};

const cellDensity: Record<TableDensity, string> = {
  compact: 'py-1.5',
  comfortable: 'py-2.5',
};

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, scope = 'col', ...props }, ref) => {
  const density = React.useContext(TableDensityContext);
  return (
    <th
      ref={ref}
      scope={scope}
      data-slot="table-head"
      className={cn(
        headDensity[density],
        'whitespace-nowrap px-3 text-start align-middle font-mono text-[0.6875rem] font-normal uppercase tracking-[0.12em] text-[var(--color-foreground-3)]',
        className,
      )}
      {...props}
    />
  );
});
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const density = React.useContext(TableDensityContext);
  return (
    <td
      ref={ref}
      data-slot="table-cell"
      className={cn(cellDensity[density], 'px-3 align-middle', className)}
      {...props}
    />
  );
});
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    data-slot="table-caption"
    className={cn('mt-2 text-start text-xs text-[var(--color-foreground-3)]', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
  type TableDensity,
};
